import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import compose from 'lodash/fp/compose';
import { withTranslation, withAccount, withUserPreferences } from 'components/hoc';
import axios from '../../axios';
import { Button } from '../common/FormItems';
import API from 'actions/api';
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { ENABLE_USER_SUBSCRIPTION_PLAN } from 'actions';
import { useDispatch, useSelector } from 'react-redux';
import { initiateStripeCheckoutThunk } from './paymentState/paymentThunk';
import PayPalLogo from '../../assets/img/paypal.svg';

const MONTHLY_PLAN = 'monthly';
const YEARLY_PLAN = 'yearly';
const MONTHLY_DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const YEARLY_DURATION_OPTIONS = [1, 2, 3];

const pricingDarkTheme = `
  --pricing-page-bg: #232c37;
  --pricing-primary: #d8eaff;
  --pricing-body: #f4f7fb;
  --pricing-muted: #b8c8de;
  --pricing-muted-strong: #9fb1ca;
  --pricing-surface: #121a24;
  --pricing-surface-soft: #182332;
  --pricing-border: #3b4d63;
  --pricing-card-hover-border: #f5b65f;
  --pricing-selected: #76a9ff;
  --pricing-selected-control: #6d9bd5;
  --pricing-selected-hover: #5f8fc9;
  --pricing-on-selected: #ffffff;
  --pricing-disabled: #536171;
  --pricing-focus: rgba(118, 169, 255, 0.24);
  --pricing-focus-soft: rgba(118, 169, 255, 0.18);
  --pricing-select-focus-border: #9ec5ff;
  --pricing-notice-bg: #121a24;
  --pricing-notice-border: #76a9ff;
  --pricing-highlight: #d8eaff;
  --pricing-badge-bg: rgba(255, 195, 111, 0.16);
  --pricing-badge-text: #ffc36f;
  --pricing-strong-badge-bg: #0d5aa0;
  --pricing-strong-badge-text: #ffffff;
  --pricing-security-bg: #172b46;
  --pricing-security-text: #9ec5ff;
  --pricing-method-paypal-bg: #20334a;
  --pricing-method-stripe-bg: #2a2d32;
  --pricing-alert-success-bg: #143522;
  --pricing-alert-success-border: #2f7d4f;
  --pricing-alert-success-text: #b7f4cb;
  --pricing-alert-error-bg: #3a1820;
  --pricing-alert-error-border: #9b3b4f;
  --pricing-alert-error-text: #ffd0d8;
  --pricing-expired-bg: #39280e;
  --pricing-expired-border: #8b6f31;
  --pricing-expired-text: #ffd58a;
`;

const PageContainer = styled.div`
  --pricing-page-bg: #ffffff;
  --pricing-primary: #083f7c;
  --pricing-body: #132033;
  --pricing-muted: #526886;
  --pricing-muted-strong: #667a98;
  --pricing-surface: #ffffff;
  --pricing-surface-soft: #f7f9fc;
  --pricing-border: #dbe6f3;
  --pricing-card-hover-border: #ffd36a;
  --pricing-selected: #2f6fd6;
  --pricing-selected-control: #5b8cca;
  --pricing-selected-hover: #4d7fbe;
  --pricing-on-selected: #ffffff;
  --pricing-disabled: #a9b9cb;
  --pricing-focus: rgba(47, 111, 214, 0.18);
  --pricing-focus-soft: rgba(47, 111, 214, 0.12);
  --pricing-select-focus-border: #7ba1d5;
  --pricing-notice-bg: #ffffff;
  --pricing-notice-border: #2f6fd6;
  --pricing-highlight: #083f7c;
  --pricing-badge-bg: #fff2cf;
  --pricing-badge-text: #9b4b00;
  --pricing-strong-badge-bg: #083f7c;
  --pricing-strong-badge-text: #ffffff;
  --pricing-security-bg: #eef5ff;
  --pricing-security-text: #3f75bd;
  --pricing-method-paypal-bg: #eaf3ff;
  --pricing-method-stripe-bg: #f4f7fb;
  --pricing-alert-success-bg: #d4edda;
  --pricing-alert-success-border: #c3e6cb;
  --pricing-alert-success-text: #155724;
  --pricing-alert-error-bg: #f8d7da;
  --pricing-alert-error-border: #f5c6cb;
  --pricing-alert-error-text: #721c24;
  --pricing-expired-bg: #fff7e6;
  --pricing-expired-border: #ffd58a;
  --pricing-expired-text: #7a4a00;

  width: 100%;
  background: var(--pricing-page-bg);
  color: var(--pricing-body);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;

  .dark-mode .authenticated &[data-pricing-theme-scope='app'] {
    ${pricingDarkTheme}
  }

  .public-dark-mode &[data-pricing-theme-scope='public'],
  [data-public-theme='dark'] &[data-pricing-theme-scope='public'],
  [data-theme='dark'] .public-theme &[data-pricing-theme-scope='public'] {
    ${pricingDarkTheme}
  }
`;

const PricingShell = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 20px 40px;

  @media (max-width: 768px) {
    padding: 0 14px 32px;
  }
`;

const HeaderBlock = styled.div`
  margin: 0 auto;
  max-width: 760px;
  padding: 40px 20px 0;
  text-align: center;

  @media (max-width: 640px) {
    padding-top: 32px;
  }
`;

const PageTitle = styled.h2`
  color: var(--pricing-primary);
  font-size: 38px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.18;
  margin: 0 0 16px;

  @media (max-width: 640px) {
    font-size: 30px;
  }
`;

const IntroText = styled.p`
  color: var(--pricing-muted);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.75;
  max-width: 620px;
  margin: 0;
  margin-left: auto;
  margin-right: auto;
`;

const Notice = styled.div`
  align-items: center;
  background: var(--pricing-notice-bg);
  border: 1px solid var(--pricing-notice-border);
  border-radius: 10px;
  color: var(--pricing-body);
  display: flex;
  font-size: 15px;
  gap: 16px;
  justify-content: center;
  line-height: 1.5;
  margin: 38px auto 42px;
  max-width: 914px;
  min-height: 64px;
  padding: 16px 24px;
  width: 100%;

  @media (max-width: 640px) {
    font-size: 14px;
    gap: 12px;
    margin-top: 28px;
    padding: 14px 16px;
  }
`;

const NoticeText = styled.span`
  color: var(--pricing-highlight);
  display: block;
  font-weight: 800;
  text-align: center;
  width: 100%;
`;

const NoticeHighlight = styled.span`
  color: inherit;
  font-weight: 800;
`;

const ContentGrid = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const PlansGrid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(2, minmax(0, 445px));
  justify-content: center;
  width: 100%;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    max-width: 445px;
  }
`;

const PlanCard = styled.div`
  background: var(--pricing-surface);
  border: 1.5px solid
    ${({ selected }) =>
      selected ? 'var(--pricing-selected)' : 'var(--pricing-border)'};
  border-radius: 14px;
  box-shadow: none;
  color: var(--pricing-primary);
  cursor: ${({ interactive }) => (interactive ? 'pointer' : 'default')};
  min-height: 358px;
  padding: 32px 32px 31px;
  position: relative;
  text-align: left;
  transition: border-color 0.2s ease;
  width: 100%;

  &:hover {
    border-color: ${({ interactive, selected }) =>
      interactive
        ? selected
          ? 'var(--pricing-selected)'
          : 'var(--pricing-card-hover-border)'
        : 'var(--pricing-border)'};
  }

  &:focus {
    outline: 3px solid var(--pricing-focus);
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    min-height: auto;
    padding: 28px 24px;
  }
`;

const SelectionIndicator = styled.button`
  align-items: center;
  background: ${({ selected }) =>
    selected ? 'var(--pricing-selected-control)' : 'var(--pricing-surface)'};
  border: 2px solid
    ${({ selected }) =>
      selected ? 'var(--pricing-selected-control)' : 'var(--pricing-border)'};
  border-radius: 999px;
  box-sizing: border-box;
  color: var(--pricing-on-selected);
  cursor: pointer;
  display: flex;
  height: 26px;
  justify-content: center;
  line-height: 0;
  padding: 0;
  position: absolute;
  right: 16px;
  top: 16px;
  width: 26px;

  svg {
    color: var(--pricing-on-selected);
    display: block;
    height: 14px;
    stroke: currentColor;
    width: 14px;
  }
`;

const PlanHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 29px;
  padding-right: 168px;

  @media (max-width: 520px) {
    padding-right: 28px;
  }
`;

const PlanTitleGroup = styled.div`
  min-width: 0;
  position: relative;
  z-index: 1;
`;

const PlanTitleLine = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-bottom: 2px;
`;

const PlanArtwork = styled.div`
  align-items: center;
  display: flex;
  font-size: 6rem;
  height: 160px;
  justify-content: center;
  opacity: 0.75;
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 22px;
  width: 180px;
  z-index: 0;

  @media (max-width: 520px) {
    display: none;
  }
`;

const DiscountBadge = styled.span`
  background: var(--pricing-badge-bg);
  border-radius: 999px;
  color: var(--pricing-badge-text);
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  padding: 5px 8px;
  white-space: nowrap;
`;

const BestValueBadge = styled.div`
  align-items: center;
  background: var(--pricing-strong-badge-bg);
  border-radius: 999px;
  color: var(--pricing-strong-badge-text);
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  height: 28px;
  justify-content: center;
  left: 50%;
  letter-spacing: 0.04em;
  min-width: 106px;
  padding: 0 16px;
  position: absolute;
  text-transform: uppercase;
  top: -15px;
  transform: translateX(-50%);
`;

const PlanName = styled.h3`
  color: var(--pricing-primary);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.1;
  margin: 0;
`;

const PlanSubtitle = styled.p`
  color: var(--pricing-muted);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
`;

const PriceLine = styled.div`
  align-items: baseline;
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const Price = styled.span`
  color: var(--pricing-primary);
  font-size: 30px;
  font-weight: 900;
  line-height: 1;
`;

const Period = styled.span`
  color: var(--pricing-muted);
  font-size: 15px;
  font-weight: 500;
`;

const FeatureText = styled.p`
  color: var(--pricing-muted);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
  margin: 0 0 29px;
  max-width: 315px;
`;

const DurationLabel = styled.div`
  color: var(--pricing-muted-strong);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const DurationOptions = styled.div`
  position: relative;
`;

const DurationSelect = styled.select`
  appearance: none;
  background: var(--pricing-surface);
  border: 1px solid var(--pricing-border);
  border-radius: 9px;
  color: var(--pricing-body);
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  height: 42px;
  padding: 0 42px 0 16px;
  width: 100%;

  &:focus {
    border-color: var(--pricing-select-focus-border);
    outline: 3px solid var(--pricing-focus-soft);
    outline-offset: 2px;
  }
`;

const SelectChevron = styled(Icon)`
  &&& {
    color: var(--pricing-muted);
    font-size: 14px;
    margin: 0;
    pointer-events: none;
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const VariationNote = styled.p`
  color: var(--pricing-muted);
  font-size: 12px;
  line-height: 1.55;
  margin: 10px 0 0;
`;

const PlanInfoNote = styled.div`
  background: var(--pricing-surface-soft);
  border-radius: 9px;
  color: var(--pricing-muted);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  margin-top: 22px;
  padding: 13px 15px;
`;

const PaymentSection = styled.div`
  align-items: flex-start;
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(0, 565px) 320px;
  justify-content: center;
  margin-top: 16px;
  max-width: 914px;
  width: 100%;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    max-width: 565px;
  }
`;

const PaymentMethodsPanel = styled.section`
  width: 100%;
`;

const PaymentSectionTitle = styled.h3`
  color: var(--pricing-primary);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.25;
  margin: 0 0 20px;
`;

const CheckoutPanel = styled.aside`
  background: var(--pricing-surface);
  border: 1px solid var(--pricing-border);
  border-radius: 14px;
  box-shadow: none;
  padding: 29px 28px 31px;
  width: 100%;

  @media (max-width: 640px) {
    padding: 24px 22px;
  }
`;

const PanelTitle = styled.h3`
  align-items: center;
  color: var(--pricing-primary);
  display: flex;
  font-size: 17px;
  font-weight: 800;
  gap: 9px;
  line-height: 1.25;
  margin: 0 0 30px;
`;

const OrderSummaryIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 21px;
  line-height: 1;
`;

const PaymentMethodList = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 20px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const MethodButton = styled.button`
  align-items: center;
  background: var(--pricing-surface);
  border: 2px solid
    ${({ active }) => (active ? 'var(--pricing-selected)' : 'var(--pricing-border)')};
  border-radius: 10px;
  color: var(--pricing-body);
  cursor: pointer;
  display: flex;
  gap: 13px;
  min-height: 104px;
  padding: 19px 16px 18px 20px;
  text-align: left;
  transition: border-color 0.2s ease;
  width: 100%;

  &:hover {
    border-color: ${({ active }) =>
      active ? 'var(--pricing-selected)' : 'var(--pricing-card-hover-border)'};
  }

  &:focus {
    outline: 3px solid var(--pricing-focus);
    outline-offset: 2px;
  }
`;

const PaymentMethodIcon = styled.div`
  align-items: center;
  background: var(--pricing-method-paypal-bg);
  border-radius: 7px;
  display: inline-flex;
  flex: 0 0 39px;
  font-size: 22px;
  height: 39px;
  justify-content: center;
  line-height: 1;
  width: 39px;

  img {
    display: block;
    max-height: 27px;
    max-width: 100%;
  }

  &.payment-method-icon--paypal {
    background: var(--pricing-surface);
    flex-basis: 78px;
    padding: 0 6px;
    width: 78px;
  }

  &.payment-method-icon--stripe {
    background: var(--pricing-method-stripe-bg);
  }
`;

const MethodContent = styled.span`
  display: block;
  flex: 1;
  min-width: 0;
`;

const MethodName = styled.span`
  color: var(--pricing-primary);
  display: block;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 5px;
`;

const MethodDescription = styled.span`
  color: var(--pricing-muted);
  display: block;
  font-size: 13px;
  line-height: 1.55;
`;

const MethodRadio = styled.span`
  align-items: center;
  align-self: center;
  background: ${({ active }) =>
    active ? 'var(--pricing-selected-control)' : 'var(--pricing-surface)'};
  border: 2px solid
    ${({ active }) =>
      active ? 'var(--pricing-selected-control)' : 'var(--pricing-border)'};
  border-radius: 999px;
  display: inline-flex;
  flex: 0 0 20px;
  height: 20px;
  justify-content: center;
  width: 20px;

  &::after {
    background: var(--pricing-on-selected);
    border-radius: 999px;
    content: '';
    display: ${({ active }) => (active ? 'block' : 'none')};
    height: 6px;
    width: 6px;
  }
`;

const SecureNotice = styled.div`
  align-items: center;
  background: var(--pricing-security-bg);
  border-radius: 8px;
  color: var(--pricing-security-text);
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 10px;
  line-height: 1.4;
  min-height: 42px;
  padding: 12px 16px;

  i.icon {
    color: var(--pricing-security-text);
    flex: 0 0 auto;
    margin: 0;
  }
`;

const Divider = styled.hr`
  background: var(--pricing-border);
  border: 0;
  height: 1px;
  margin: 10px 0;
`;

const SummaryRows = styled.div`
  display: grid;
  gap: 0;
`;

const SummaryRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  padding: 9px 0;
`;

const SummaryLabel = styled.span`
  color: var(--pricing-muted);
  font-size: 14px;
  line-height: 1.4;
`;

const SummaryValue = styled.span`
  color: var(--pricing-primary);
  font-size: 14px;
  font-weight: 800;
  line-height: 1.4;
  text-align: right;
`;

const Statement = styled.div`
  background: var(--pricing-surface-soft);
  border-radius: 10px;
  color: var(--pricing-body);
  font-size: 13px;
  line-height: 1.5;
  margin-top: 16px;
  padding: 16px;
`;

const StatementLabel = styled.div`
  color: var(--pricing-muted-strong);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
  text-transform: uppercase;
`;

const StatementText = styled.div`
  color: var(--pricing-body);
  font-size: 14px;
  font-weight: 500;
`;

const StatementAmount = styled.div`
  color: var(--pricing-selected);
  font-size: 14px;
  font-weight: 800;
`;

const TotalRow = styled.div`
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-top: 27px;
`;

const TotalLabel = styled.span`
  color: var(--pricing-body);
  font-size: 15px;
  font-weight: 800;
`;

const TotalValue = styled.span`
  color: var(--pricing-selected-control);
  font-size: 32px;
  font-weight: 900;
  line-height: 1;
`;

const PayButton = styled(Button)`
  background: ${({ disabled }) =>
    disabled ? 'var(--pricing-disabled)' : 'var(--pricing-selected-control)'};
  border: 1px solid
    ${({ disabled }) =>
      disabled ? 'var(--pricing-disabled)' : 'var(--pricing-selected-control)'};
  border-radius: 8px;
  color: var(--pricing-on-selected);
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-size: 15px;
  font-weight: 800;
  margin: 23px 0 0;
  min-height: 54px;
  padding: 12px 18px;
  width: 100%;

  span.block {
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 8px;
  }

  &:hover {
    background: ${({ disabled }) =>
      disabled ? 'var(--pricing-disabled)' : 'var(--pricing-selected-hover)'};
    border-color: ${({ disabled }) =>
      disabled ? 'var(--pricing-disabled)' : 'var(--pricing-selected-hover)'};
  }
`;

const PayNowIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    focusable="false"
    height="16"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
    width="16"
  >
    <rect height="11" rx="2" width="18" x="3" y="11" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const TermsText = styled.p`
  color: var(--pricing-muted);
  font-size: 12px;
  line-height: 1.55;
  margin: 12px 0 0;
  text-align: center;
`;

const TermsLink = styled(Link)`
  color: var(--pricing-selected);
  text-decoration: underline;
`;

const AlertBox = styled.div`
  background: ${({ success }) =>
    success ? 'var(--pricing-alert-success-bg)' : 'var(--pricing-alert-error-bg)'};
  border: 1px solid
    ${({ success }) =>
      success
        ? 'var(--pricing-alert-success-border)'
        : 'var(--pricing-alert-error-border)'};
  border-radius: 8px;
  color: ${({ success }) =>
    success
      ? 'var(--pricing-alert-success-text)'
      : 'var(--pricing-alert-error-text)'};
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 18px;
  padding: 13px 15px;
`;

const ExpiredBanner = styled.div`
  background: var(--pricing-expired-bg);
  border: 1px solid var(--pricing-expired-border);
  border-radius: 8px;
  color: var(--pricing-expired-text);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 20px;
  padding: 14px 16px;
`;

const PublicPricingNote = styled.div`
  background: var(--pricing-security-bg);
  border-radius: 10px;
  color: var(--pricing-security-text);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.55;
  max-width: 914px;
  padding: 16px 22px;
  text-align: center;
  width: 100%;
`;

const SelectionCheckIcon = () => (
  <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16">
    <path
      d="M3.5 8.1 6.6 11.2 12.7 4.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
    />
  </svg>
);

const formatCurrency = (value, digits = 0) => {
  const numberValue = Number(value || 0);
  return `${numberValue.toFixed(digits)} €`;
};

const getDurationLabel = (tr, planType, duration) => {
  if (!duration) return tr('No duration selected');

  const durationCount = Number(duration);
  const isPlural = durationCount > 1;
  const unit =
    planType === MONTHLY_PLAN
      ? tr(isPlural ? 'months' : 'month')
      : tr(isPlural ? 'years' : 'year');

  return `${duration} ${unit}`;
};

const PricingPlanCard = ({
  badge,
  defaultDuration,
  description,
  durationOptions,
  isSelected,
  onDurationSelect,
  onSelect,
  artwork,
  period,
  planType,
  price,
  readOnly,
  selectedDuration,
  showBestValue,
  showPriceVariation,
  subtitle,
  title,
  tr,
  unitCost,
}) => {
  const displayDuration = selectedDuration || defaultDuration;
  const showInteractiveControls = !readOnly;

  return (
    <PlanCard
      aria-pressed={showInteractiveControls ? isSelected : undefined}
      interactive={showInteractiveControls}
      onClick={showInteractiveControls ? onSelect : undefined}
      selected={showInteractiveControls && isSelected}
    >
      {showInteractiveControls && (
        <SelectionIndicator
          aria-label={tr(`Select ${planType} plan`)}
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          selected={isSelected}
          type="button"
        >
          {isSelected && <SelectionCheckIcon />}
        </SelectionIndicator>
      )}

      {showBestValue && <BestValueBadge>{tr('Best Value')}</BestValueBadge>}

      <PlanHeader>
        <PlanTitleGroup>
          <PlanTitleLine>
            <PlanName>{title}</PlanName>
            {badge && <DiscountBadge>{badge}</DiscountBadge>}
          </PlanTitleLine>
          <PlanSubtitle>{subtitle}</PlanSubtitle>
        </PlanTitleGroup>

        <PlanArtwork>{artwork === 'ticket' ? '🎫' : '🍽️'}</PlanArtwork>
      </PlanHeader>

      <PriceLine>
        <Price>{Number(price).toFixed(0)}€</Price>
        <Period>/ {period}</Period>
      </PriceLine>
      <FeatureText>{description}</FeatureText>

      {showInteractiveControls ? (
        <>
          <DurationLabel>{tr('Select duration')}</DurationLabel>
          <DurationOptions>
            <DurationSelect
              aria-label={tr('Select duration')}
              onChange={(event) => onDurationSelect(Number(event.target.value))}
              onClick={(event) => event.stopPropagation()}
              value={displayDuration}
            >
              {durationOptions.map((duration) => (
                <option key={duration} value={duration}>
                  {getDurationLabel(tr, planType, duration)}
                </option>
              ))}
            </DurationSelect>
            <SelectChevron name="chevron down" />
          </DurationOptions>
        </>
      ) : (
        <PlanInfoNote>
          {planType === MONTHLY_PLAN
            ? tr('Flexible monthly subscription information')
            : tr('Best value annual subscription information')}
        </PlanInfoNote>
      )}

      {showInteractiveControls && showPriceVariation && isSelected && (
        <VariationNote>
          {`${tr('For subscription of')} ${selectedDuration} ${
            Number(selectedDuration) > 1 ? tr('months') : tr('month')
          } ${tr('you will be charged at')} ${formatCurrency(unitCost, 2)} ${tr(
            'per month',
          )}`}
        </VariationNote>
      )}
    </PlanCard>
  );
};

const PaymentPage = ({ tr, fd, subscriptionEndDate, isPaymentNeeded, account }) => {
  const [selectedCard, setSelectedCard] = useState(YEARLY_PLAN);
  const [year, setYear] = useState(1);
  const [month, setMonth] = useState('');
  const [yearCost] = useState(19);
  const [monthCost, setMonthCost] = useState(2);
  const [loading, setLoading] = useState(false);
  const [showPriceVariation, setShowPriceVariation] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [msgInfo] = useState({});

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuth = Boolean(account && account.user && account.user.isAuth);
  const isPublicPricing = !isAuth;

  const { checkoutLoading, checkoutError } = useSelector((state) => state.payment);

  const monthlyPlanLabel = tr(MONTHLY_PLAN);
  const yearlyPlanLabel = tr(YEARLY_PLAN);
  const isMonthlySelected = selectedCard === MONTHLY_PLAN;
  const isYearlySelected = selectedCard === YEARLY_PLAN;
  let selectedPlanType = '';
  let selectedPlanName = tr('No plan selected');

  if (isMonthlySelected) {
    selectedPlanType = MONTHLY_PLAN;
    selectedPlanName = tr('Monthly Plan');
  } else if (isYearlySelected) {
    selectedPlanType = YEARLY_PLAN;
    selectedPlanName = tr('Yearly Plan');
  }

  const selectedDuration = isMonthlySelected ? month : isYearlySelected ? year : '';
  const selectedUnitCost = isMonthlySelected ? monthCost : yearCost;
  const totalDue = selectedDuration
    ? Number(selectedDuration) * selectedUnitCost
    : 0;
  const canPay = isAuth && Boolean(selectedCard && selectedDuration);
  const isSubmitting = loading || checkoutLoading;
  const stripeButtonText = checkoutLoading
    ? tr('Redirecting...')
    : tr('Pay with Stripe');

  const plans = useMemo(
    () => [
      {
        artwork: 'ticket',
        badge: null,
        defaultDuration: 1,
        description: tr(
          'Less than the price of a local bus ticket. Perfect for those who want ultimate precision without a long term commitment.',
        ),
        durationOptions: MONTHLY_DURATION_OPTIONS,
        period: tr('month'),
        planType: MONTHLY_PLAN,
        price: 2,
        selectedDuration: month,
        showBestValue: false,
        showPriceVariation,
        subtitle: tr('Flexible performance'),
        title: monthlyPlanLabel,
        unitCost: monthCost,
      },
      {
        artwork: 'meal',
        badge: tr('21% OFF'),
        defaultDuration: 1,
        description: tr(
          'The price of a single great meal for a whole year of precision. Engineered for those committed to excellence.',
        ),
        durationOptions: YEARLY_DURATION_OPTIONS,
        period: tr('year'),
        planType: YEARLY_PLAN,
        price: yearCost,
        selectedDuration: year,
        showBestValue: true,
        showPriceVariation: false,
        subtitle: tr('Engineered for long-term growth'),
        title: yearlyPlanLabel,
        unitCost: yearCost,
      },
    ],
    [
      month,
      monthCost,
      monthlyPlanLabel,
      showPriceVariation,
      tr,
      year,
      yearCost,
      yearlyPlanLabel,
    ],
  );

  React.useEffect(() => {
    if (!isAuth) {
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const action = queryParams.get('action');
    const token = queryParams.get('token');

    let message = '';
    let messageDetails = '';
    let type = '';

    if (action) {
      if (action === 'cancelled') {
        message = tr('Transaction Cancelled');
        messageDetails = tr(
          'The transaction has been cancelled, you will be redireced back',
        );
        type = 'error';
      } else if (action === 'approved') {
        message = tr('Transaction Successful');
        messageDetails = tr(
          'The transaction was successful, you will be redirected back',
        );
        type = 'success';
      }

      if (action === 'approved' || action === 'cancelled') {
        axios
          .post(`${API}/payment/payment_completed/`, { token, action })
          .then(() => {
            if (action === 'approved') {
              dispatch({ type: ENABLE_USER_SUBSCRIPTION_PLAN, payload: {} });

              const user = JSON.parse(localStorage.getItem('user'));
              user.has_active_plan = true;
              localStorage.setItem('user', JSON.stringify(user));
            }

            setLoading(false);
          })
          .catch((error) => {
            setLoading(false);
            console.error('Error confirming transaction:', error);
          });
      }

      Swal.fire(tr(message), tr(messageDetails), type).then(() => {
        navigate('/dashboard/settings');
      });
    }
  }, [dispatch, navigate, isAuth, location, tr]);

  const selectCard = (card) => {
    setSelectedCard(card);

    if (card === MONTHLY_PLAN && !month) {
      setMonthValue(1);
    }

    if (card === YEARLY_PLAN && !year) {
      setYearValue(1);
    }
  };

  const setMonthValue = (value) => {
    const numericValue = Number(value);
    setMonth(numericValue);
    setSelectedCard(MONTHLY_PLAN);

    setMonthCost(2);

    if (Number.isFinite(numericValue) && numericValue === 1) {
      setShowPriceVariation(true);
      setMonthCost(3.5);
    } else if (Number.isFinite(numericValue) && numericValue === 2) {
      setShowPriceVariation(true);
      setMonthCost(2.5);
    } else {
      setShowPriceVariation(false);
    }
  };

  const setYearValue = (value) => {
    setYear(value);
    setSelectedCard(YEARLY_PLAN);
    setShowPriceVariation(false);
    setMonthCost(2);
  };

  const validatePlanChoice = () => {
    if (!selectedCard) {
      Swal.fire(tr('Warning!'), tr('Please select a plan.'), 'warning');
      return false;
    }

    if (isMonthlySelected && !month) {
      Swal.fire(tr('Warning!'), tr('Please select number of months.'), 'warning');
      return false;
    }

    if (isYearlySelected && !year) {
      Swal.fire(tr('Warning!'), tr('Please select number of years.'), 'warning');
      return false;
    }

    return true;
  };

  const getPaymentPayload = () => ({
    amount: isMonthlySelected ? monthCost : yearCost,
    duration: isMonthlySelected ? month : year,
    subscriptionType: isMonthlySelected ? 'month' : 'year',
  });

  const pay = async () => {
    try {
      setLoading(true);

      if (!validatePlanChoice()) {
        return;
      }

      const payload = getPaymentPayload();
      const response = await axios.post(`${API}/payment/initiate_payment/`, {
        subscription_type: payload.subscriptionType,
        amount: payload.amount,
        duration: payload.duration,
        url: window.location.href,
      });

      Swal.fire(
        tr('Success!'),
        tr(
          'Your payment has been initiated, you will be redirected to paypal to complete this payment',
        ),
        'success',
      ).then(() => {
        const data = response.data;
        const approveLink = data.links.find((link) => link.rel === 'payer-action');

        if (approveLink && approveLink.href) {
          window.location.href = approveLink.href;
        } else {
          Swal.fire(tr('Error!'), tr('Unable to find approval URL.'), 'error');
        }
      });
    } catch (error) {
      Swal.fire(
        tr('Error!'),
        tr('There was an issue creating this payment. Please contact administrator'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStripeCheckout = () => {
    if (!validatePlanChoice()) {
      return;
    }

    const payload = getPaymentPayload();
    dispatch(
      initiateStripeCheckoutThunk(
        payload.subscriptionType,
        payload.amount,
        payload.duration,
      ),
    );
  };

  const handleCheckout = () => {
    if (paymentMethod === 'paypal') {
      pay();
      return;
    }

    handleStripeCheckout();
  };

  return (
    <PageContainer data-pricing-theme-scope={isPublicPricing ? 'public' : 'app'}>
      <PricingShell>
        {isAuth && isPaymentNeeded && (
          <ExpiredBanner>
            {tr('You see this page, because your subscription has ended on')}{' '}
            {fd(subscriptionEndDate)}.{' '}
            {tr(
              'Please renew your subscription, if you like to keep using our services. Thank you!',
            )}
          </ExpiredBanner>
        )}

        <HeaderBlock>
          <PageTitle>
            {isPublicPricing
              ? tr('AdminCat Pricing')
              : tr('Choose your Payment Plan')}
          </PageTitle>
          <IntroText>
            {isPublicPricing
              ? tr(
                  'Compare AdminCat plans and choose the right fit after creating your account.',
                )
              : tr(
                  'Whether you want to get organized, keep your personal life on track, or boost workplace productivity, AdminCat has the right plan for you.',
                )}
          </IntroText>
        </HeaderBlock>

        <Notice>
          <NoticeText>
            {isPublicPricing ? (
              <>
                {tr('Create an account for FREE and enjoy your')}{' '}
                <NoticeHighlight>{tr('free trial for 30 days.')}</NoticeHighlight>
              </>
            ) : (
              <>
                {tr('Before subscribing you can register for FREE and enjoy your')}{' '}
                <NoticeHighlight>{tr('free trial for 30 days.')}</NoticeHighlight>
              </>
            )}
          </NoticeText>
        </Notice>

        {msgInfo.msg && msgInfo.msg.length > 0 && (
          <AlertBox success={msgInfo.success}>
            {msgInfo.msg.map((message, index) => (
              <div key={index}>{message}</div>
            ))}
          </AlertBox>
        )}

        <ContentGrid>
          <PlansGrid>
            {plans.map((plan) => (
              <PricingPlanCard
                {...plan}
                isSelected={selectedCard === plan.planType}
                key={plan.planType}
                onDurationSelect={
                  plan.planType === MONTHLY_PLAN ? setMonthValue : setYearValue
                }
                onSelect={() => selectCard(plan.planType)}
                readOnly={isPublicPricing}
                tr={tr}
              />
            ))}
          </PlansGrid>

          {isPublicPricing ? (
            <PublicPricingNote>
              {tr(
                'Payment options are available after sign in. This page is for pricing information only.',
              )}
            </PublicPricingNote>
          ) : (
            <PaymentSection>
              <PaymentMethodsPanel>
                <PaymentSectionTitle>
                  {tr('Select Payment Method')}
                </PaymentSectionTitle>

                <PaymentMethodList>
                  <MethodButton
                    active={paymentMethod === 'paypal'}
                    aria-pressed={paymentMethod === 'paypal'}
                    onClick={() => setPaymentMethod('paypal')}
                    type="button"
                  >
                    <PaymentMethodIcon className="payment-method-icon payment-method-icon--paypal">
                      <img src={PayPalLogo} alt="" aria-hidden="true" />
                    </PaymentMethodIcon>
                    <MethodContent>
                      <MethodName>{tr('PayPal')}</MethodName>
                      <MethodDescription>
                        {tr('Pay with balance or credit card')}
                      </MethodDescription>
                    </MethodContent>
                    <MethodRadio active={paymentMethod === 'paypal'} />
                  </MethodButton>

                  <MethodButton
                    active={paymentMethod === 'stripe'}
                    aria-pressed={paymentMethod === 'stripe'}
                    onClick={() => setPaymentMethod('stripe')}
                    type="button"
                  >
                    <PaymentMethodIcon className="payment-method-icon payment-method-icon--stripe">
                      💳
                    </PaymentMethodIcon>
                    <MethodContent>
                      <MethodName>{tr('Stripe')}</MethodName>
                      <MethodDescription>
                        {tr('Direct secure credit card processing')}
                      </MethodDescription>
                    </MethodContent>
                    <MethodRadio active={paymentMethod === 'stripe'} />
                  </MethodButton>
                </PaymentMethodList>

                <SecureNotice>
                  <Icon name="shield" />
                  <span>
                    {tr(
                      'Secure checkout powered by industry standard 256-bit encryption.',
                    )}
                  </span>
                </SecureNotice>

                {checkoutError && paymentMethod === 'stripe' && (
                  <AlertBox success={false}>{checkoutError}</AlertBox>
                )}
              </PaymentMethodsPanel>

              <CheckoutPanel>
                <PanelTitle>
                  <OrderSummaryIcon aria-hidden="true">🛒</OrderSummaryIcon>
                  {tr('Order Summary')}
                </PanelTitle>

                <SummaryRows>
                  <SummaryRow>
                    <SummaryLabel>{tr('Subscription')}</SummaryLabel>
                    <SummaryValue>{selectedPlanName}</SummaryValue>
                  </SummaryRow>
                  <Divider />
                  <SummaryRow>
                    <SummaryLabel>{tr('Term Duration')}</SummaryLabel>
                    <SummaryValue>
                      {getDurationLabel(tr, selectedPlanType, selectedDuration)}
                    </SummaryValue>
                  </SummaryRow>
                  <Divider />
                </SummaryRows>

                <Statement>
                  <StatementLabel>{tr('Statement')}</StatementLabel>
                  {canPay ? (
                    <>
                      <StatementText>{tr('Your payment choice:')}</StatementText>
                      <StatementAmount>
                        {`${getDurationLabel(
                          tr,
                          selectedPlanType,
                          selectedDuration,
                        )} x ${formatCurrency(
                          selectedUnitCost,
                          2,
                        )} = ${formatCurrency(totalDue, 2)}`}
                      </StatementAmount>
                    </>
                  ) : (
                    <StatementText>
                      {tr('Choose a plan to see your order total.')}
                    </StatementText>
                  )}
                </Statement>

                <TotalRow>
                  <TotalLabel>{tr('Total Due')}</TotalLabel>
                  <TotalValue>{formatCurrency(totalDue, 2)}</TotalValue>
                </TotalRow>

                <PayButton
                  disabled={!canPay || isSubmitting}
                  isLoading={isSubmitting}
                  onClick={canPay && !isSubmitting ? handleCheckout : undefined}
                  render={({ isLoading }) => (
                    <span className="block">
                      {isLoading ? (
                        <i className="fa fa-spinner fa-spin"></i>
                      ) : (
                        <>
                          <span>
                            {paymentMethod === 'paypal'
                              ? tr('Pay Now')
                              : stripeButtonText}
                          </span>
                          <PayNowIcon />
                        </>
                      )}
                    </span>
                  )}
                />

                <TermsText>
                  {tr("By clicking Pay Now, you agree to AdminCat's")}{' '}
                  <TermsLink to="/user-terms">{tr('User Terms')}</TermsLink>{' '}
                  {tr('payment terms and')}{' '}
                  <TermsLink to="/privacy-statement">
                    {tr('Privacy Policy')}
                  </TermsLink>.
                </TermsText>
              </CheckoutPanel>
            </PaymentSection>
          )}
        </ContentGrid>
      </PricingShell>
    </PageContainer>
  );
};

export default compose(
  withAccount,
  withUserPreferences,
  withTranslation,
)(PaymentPage);
